import React, { useEffect } from 'react';
import * as yup from 'yup';
import { Grid } from '@mui/material';
import { ICommentForThreadFormValues } from 'models/cases';
import { useTranslations } from 'hooks/useTranslations';
import Button from 'components/atoms/Button';
import { Box } from '@mui/material';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { countEditorCharacters } from 'components/organisms/HtmlSlate';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormWyswigEditor } from 'components/molecules/FormWyswigEditor';
import { Elabels } from '../../helpers';

export interface ICommentForm {
  submitForm: (data: any) => void;
  closeCommentForm: () => void;
  editFormValues?: ICommentForThreadFormValues;
}
const initialFormValues: ICommentForThreadFormValues = { comment: '' };

export const CommentForm = ({
  submitForm,
  closeCommentForm,
  editFormValues,
}: ICommentForm) => {
  const { t } = useTranslations();

  const schema = yup.object({
    comment: yup
      .string()
      .required(t('general.field.validation.required'))
      .test(
        'editor-validation',
        t('general.field.validation.maxCharacters').replace('{0}', '1000'),
        (value) => countEditorCharacters(value) <= 1000,
      )
      .trim(),
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm<ICommentForThreadFormValues>({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<ICommentForThreadFormValues>,
  });

  useEffect(() => {
    reset(editFormValues);
  }, [editFormValues]);

  return (
    <Grid sx={{ p: 1 }}>
      <form onSubmit={handleSubmit(submitForm)}>
        <Grid container>
          <Grid size={{ xs: 12 }}>
            <FormWyswigEditor
              name="comment"
              label={t(Elabels.comment)}
              setValue={setValue}
              control={control}
              errors={errors}
              setError={setError}
              clearErrors={clearErrors}
              charsLimit={1000}
              required
              fullContainer
              showCounter
            />
          </Grid>
          <Grid
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', xl: 'row' },
              gap: '12px',
              mt: '20px',
              width: '100%',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Button
                fullWidth
                label={t('general.cancel.button')}
                variant={EButtonVariants.outlined}
                onClick={() => {
                  reset(initialFormValues);
                  closeCommentForm();
                }}
                size={EButtonSizes.small}
                id="comment_form_cancel-button"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Button
                variant={EButtonVariants.contained}
                label={t('general.save.button')}
                type="submit"
                fullWidth
                size={EButtonSizes.small}
                id="comment_form_save-button"
              />
            </Box>
          </Grid>
        </Grid>
      </form>
    </Grid>
  );
};
